import React, { useState, useRef, useEffect } from 'react';
import styles from './AIGeneratorButton.module.css';
import { SummaryReqBody, AIGateway } from '../../data_provider/gateways/AIGateway';
import Icon from '../common/Icon';

export interface AIGeneratorButtonProps {
    label: string;
    mapData?: any[];
    onTextGenerated: (text: string, isTyping: boolean, isReset?: boolean) => void;
    disabled?: boolean;
    maxLength?: number; // 최대 글자수 제한
    processIdx?: number; // 프로세스 인덱스 추가
}

// 버튼 상태 타입 정의
type GeneratorStatus = 'waiting' | 'connecting' | 'generating' | 'completed';

/**
 * AI 텍스트 생성 버튼 컴포넌트
 * @component AIGeneratorButton
 * @author Kim Young Jun
 * @date 2025-09-24
 */
const AIGeneratorButton: React.FC<AIGeneratorButtonProps> = ({
    label,
    mapData,
    onTextGenerated,
    disabled = false,
    maxLength,
    processIdx
}) => {
    const currentAIGateway = useRef<AIGateway | null>(null);
    const [status, setStatus] = useState<GeneratorStatus>('waiting');
    const [isRestarting, setIsRestarting] = useState(false);
    const connectingTimeoutRef = useRef<NodeJS.Timeout | null>(null);
    const currentTextLength = useRef<number>(0); // 현재 생성된 텍스트 길이 추적

    // processIdx 변경 시 스트림 중단 (MapComplete는 processIdx 5에서만 사용됨)
    useEffect(() => {
        if (processIdx !== undefined && processIdx !== 5 && (status === 'connecting' || status === 'generating')) {
            console.log('ProcessIdx 변경으로 인한 AI 생성 스트림 중단');

            // 연결 중 상태에서 타임아웃 클리어
            if (connectingTimeoutRef.current) {
                clearTimeout(connectingTimeoutRef.current);
                connectingTimeoutRef.current = null;
            }

            // AI Gateway 스트림 중단
            if (currentAIGateway.current) {
                currentAIGateway.current.abortStream();
                currentAIGateway.current = null;
            }

            // 상태를 대기로 변경
            setStatus('waiting');
            setIsRestarting(false);
            onTextGenerated('', false); // 중단 신호
        }
    }, [processIdx, status, onTextGenerated]);

    // AI 텍스트 생성 요청
    const requestGeneration = async () => {
        console.log('=== AI 텍스트 생성 요청 시작 ===');

        // 0. 새 생성 시작 시 초기화 신호 전송
        currentTextLength.current = 0; // 텍스트 길이 초기화
        onTextGenerated('', false, true); // 세 번째 파라미터로 초기화 신호

        // 1. 이전 인스턴스 완전 제거
        if (currentAIGateway.current) {
            console.log('이전 AIGateway 인스턴스 정리 중...');
            try {
                currentAIGateway.current.abortStream();
            } catch (e) {
                console.log('이전 인스턴스 정리 완료');
            }
            currentAIGateway.current = null;
        }

        // 2. 새로운 인스턴스 생성
        currentAIGateway.current = new AIGateway();
        setStatus('connecting');

        try {
            // 3. 연결 대기
            await new Promise((resolve) => {
                connectingTimeoutRef.current = setTimeout(resolve, 1000);
            });

            setStatus('generating');

            // 4. 요청 데이터 준비
            const requestBody: SummaryReqBody = {
                // mapData: mapData && mapData.length > 0 ? [
                //     Object.keys(mapData[0] || {}), // 헤더
                //     ...mapData.slice(0, 5).map(row => Object.values(row)) // 샘플 데이터 (최대 5개)
                // ] : [],
                // dataCount: mapData?.length || 0,
                // fields: mapData && mapData.length > 0 ? Object.keys(mapData[0] || {}) : [],

                // hmchoi
                mapData: mapData && mapData.length > 0 ? mapData.slice(0, 6) : [],
                dataCount: mapData && mapData.length > 0 ? Math.max(mapData.length - 1, 0) : 0,
                fields: mapData && mapData.length > 0 ? mapData[0] : [],
                type: 'description' // 설명 요청 타입 지정
            };

            console.log('요청 바디:', requestBody);

            // 5. SSE 스트림 요청
            await currentAIGateway.current!.reqSummaryStream(requestBody, {
                onChunk: (chunk: string) => {
                    // maxLength 체크
                    if (maxLength && currentTextLength.current + chunk.length > maxLength) {
                        // 제한을 초과하면 스트림 중단
                        if (currentAIGateway.current) {
                            currentAIGateway.current.abortStream();
                            currentAIGateway.current = null;
                        }
                        setStatus('completed');
                        setIsRestarting(false);
                        onTextGenerated('', false); // 타이핑 완료 신호
                        return;
                    }

                    // 길이 업데이트
                    currentTextLength.current += chunk.length;
                    // 타이핑 효과로 텍스트 전달
                    onTextGenerated(chunk, true);
                },
                onComplete: () => {
                    setStatus('completed');
                    setIsRestarting(false);
                    onTextGenerated('', false); // 타이핑 완료 신호
                },
                onError: (error: string) => {
                    console.error('AI 텍스트 생성 실패:', error);
                    const fallbackText = '텍스트 생성에 실패했습니다.';
                    onTextGenerated(fallbackText, false);
                    setStatus('completed');
                    setIsRestarting(false);
                }
            });

        } catch (error: any) {
            console.error('AI 텍스트 생성 실패:', error);
            const fallbackText = '텍스트 생성에 실패했습니다.';
            onTextGenerated(fallbackText, false);
            setStatus('completed');
            setIsRestarting(false);
        }
    };

    // 버튼 클릭 핸들러
    const handleClick = () => {
        if (disabled) return;

        switch (status) {
            case 'waiting':
            case 'completed':
                requestGeneration();
                break;

            case 'connecting':
                // 연결 중 중단
                if (connectingTimeoutRef.current) {
                    clearTimeout(connectingTimeoutRef.current);
                    connectingTimeoutRef.current = null;
                }
                setStatus('waiting');
                break;

            case 'generating':
                // 생성 중 중단
                if (currentAIGateway.current) {
                    currentAIGateway.current.abortStream();
                    currentAIGateway.current = null;
                }
                setStatus('waiting');
                onTextGenerated('', false); // 생성 중단 신호
                break;
        }
    };

    // 버튼 라벨 결정
    const getButtonLabel = () => {
        switch (status) {
            case 'waiting':
                return `AI ${label}`;
            case 'connecting':
                return '연결중';
            case 'generating':
                return '작성중';
            case 'completed':
                return `AI ${label}`;
            default:
                return `AI ${label}`;
        }
    };

    // 버튼 상태 클래스
    const getButtonClass = () => {
        const baseClass = styles.aiGeneratorButton;
        if (disabled) return `${baseClass} ${styles.disabled}`;
        if (status === 'connecting' || status === 'generating') {
            return `${baseClass} ${styles.active}`;
        }
        return baseClass;
    };

    return (
        <button
            type="button"
            className={getButtonClass()}
            onClick={handleClick}
            disabled={disabled}
        >
            <div className={styles.buttonContent}>
                {status === 'waiting' || status === 'completed' ? (
                    <img
                        className={styles.aiIcon}
                        src={require("../../img/ai_agent_logo.png")}
                        alt="AI Agent"
                    />
                ) : (
                    <div className={styles.iconContainer}>
                        <div className={styles.dotsContainer}>
                            <div className={styles.dotElastic}></div>
                        </div>
                    </div>
                )}
                <span className={`${styles.buttonLabel} ${(status === 'connecting' || status === 'generating') ? styles.animating : ''}`}>
                    {getButtonLabel()}
                </span>
            </div>
        </button>
    );
};

export default AIGeneratorButton;